// src/components/ReplyProcessingOverlay.js
import React, { useState, useEffect, useRef } from 'react';

export default function ReplyProcessingOverlay({ onComplete }) {
  const [isVisible, setIsVisible] = useState(false);
  const [processedReplies, setProcessedReplies] = useState([]);
  const [stats, setStats] = useState({ interested: 0, later: 0, optout: 0, noreply: 0 });
  const [isComplete, setIsComplete] = useState(false);
  const [currentProcessingId, setCurrentProcessingId] = useState(null);
  
  const isMounted = useRef(true);
  const processingTimerRef = useRef(null);
  const replyQueueRef = useRef([]);
  const isProcessingRef = useRef(false);

  // Sample patient replies with some marked as 'no reply'
  const sampleReplies = [
    { id: 1, patient: 'Sarah Mitchell', message: 'Yes, I would love to schedule!', category: 'interested' },
    { id: 2, patient: 'James Wong', message: 'Can we do next week instead?', category: 'later' },
    { id: 3, patient: 'Emma Thompson', message: 'Please remove me from the list.', category: 'optout' },
    { id: 4, patient: 'Michael Chen', message: 'What time slots are available?', category: 'interested' },
    { id: 5, patient: 'Jessica Davis', message: 'I\'ll get back to you next month.', category: 'later' },
    { id: 6, patient: 'David Kim', message: 'Yes, I\'ve been meaning to book!', category: 'interested' },
    { id: 7, patient: 'Rachel Patel', message: 'Not interested at this time.', category: 'optout' },
    { id: 8, patient: 'Daniel Tan', message: 'Can you call me tomorrow?', category: 'later' },
    { id: 9, patient: 'Laura Ng', message: 'Perfect, I\'ll be there!', category: 'interested' },
    { id: 10, patient: 'Kevin Low', message: 'Please don\'t contact me again.', category: 'optout' },
    { id: 11, patient: 'Amanda Lee', message: 'I\'d like to reschedule to Friday.', category: 'later' },
    { id: 12, patient: 'Christopher Goh', message: 'Yes please, my teeth need cleaning!', category: 'interested' },
    { id: 13, patient: 'Patricia Ng', message: 'No response yet', category: 'noreply' },
    { id: 14, patient: 'Robert Tan', message: 'No response yet', category: 'noreply' },
  ];

  // Fade-in animation
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
      }
    };
  }, []);

  // Initialize with all replies in 'pending' state
  useEffect(() => {
    const initial = sampleReplies.map(reply => ({
      ...reply,
      status: 'pending'
    }));
    setProcessedReplies(initial);
    
    // Initialize the queue with all reply IDs
    replyQueueRef.current = sampleReplies.map(reply => reply.id);
  }, []);

  // Process the next reply in queue
  const processNextInQueue = () => {
    if (!isMounted.current) {
      isProcessingRef.current = false;
      return;
    }

    // If queue is empty, we're done
    if (replyQueueRef.current.length === 0) {
      setIsComplete(true);
      isProcessingRef.current = false;
      const timer = setTimeout(() => {
        if (onComplete && isMounted.current) onComplete();
      }, 2500);
      return;
    }

    // Get the next reply ID from the queue
    const nextId = replyQueueRef.current[0];
    setCurrentProcessingId(nextId);

    // Find the reply and mark it as analyzing
    setProcessedReplies(prev => {
      const updated = prev.map(reply => {
        if (reply.id === nextId && reply.status === 'pending') {
          return { ...reply, status: 'analyzing' };
        }
        return reply;
      });
      return updated;
    });

    // After delay, categorize it
    processingTimerRef.current = setTimeout(() => {
      if (!isMounted.current) return;

      // Find the reply and categorize it
      setProcessedReplies(prev => {
        const updated = prev.map(reply => {
          if (reply.id === nextId && reply.status === 'analyzing') {
            // Update stats
            setStats(prevStats => {
              const newStats = { ...prevStats };
              switch(reply.category) {
                case 'interested': newStats.interested += 1; break;
                case 'later': newStats.later += 1; break;
                case 'optout': newStats.optout += 1; break;
                case 'noreply': newStats.noreply += 1; break;
                default: break;
              }
              return newStats;
            });
            
            return { ...reply, status: 'categorized' };
          }
          return reply;
        });
        return updated;
      });

      // Remove the processed ID from the queue
      replyQueueRef.current.shift();
      setCurrentProcessingId(null);

      // Process next after a short delay
      processingTimerRef.current = setTimeout(() => {
        if (isMounted.current) {
          processNextInQueue();
        }
      }, 300);
    }, 700);
  };

  // Start processing when replies are initialized
  useEffect(() => {
    if (processedReplies.length > 0 && !isProcessingRef.current && !isComplete) {
      isProcessingRef.current = true;
      // Start processing after a small delay
      const timer = setTimeout(() => {
        if (isMounted.current) {
          processNextInQueue();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [processedReplies.length, isComplete]);

  // Get category icon and style
  const getCategoryIcon = (category) => {
    switch(category) {
      case 'interested': return '👍';
      case 'later': return '⏰';
      case 'optout': return '🚫';
      case 'noreply': return '🔇';
      default: return '';
    }
  };

  const getCategoryBadgeStyle = (category) => {
    const colors = {
      interested: { bg: 'rgba(58, 191, 168, 0.15)', text: '#3ABFA8' },
      later: { bg: 'rgba(245, 158, 11, 0.15)', text: '#F59E0B' },
      optout: { bg: 'rgba(239, 68, 68, 0.15)', text: '#EF4444' },
      noreply: { bg: 'rgba(156, 163, 175, 0.15)', text: '#9CA3AF' }
    };
    return colors[category] || colors.interested;
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'interested': return '#3ABFA8';
      case 'later': return '#F59E0B';
      case 'optout': return '#EF4444';
      case 'noreply': return '#9CA3AF';
      default: return 'rgba(255,255,255,0.3)';
    }
  };

  const totalReplies = processedReplies.length;
  const processedCount = processedReplies.filter(r => r.status === 'categorized').length;

  return (
    <div style={replyStyles.overlay}>
      <div style={replyStyles.glowBg} />
      
      <div 
        style={{
          ...replyStyles.content,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        }}
      >
        {/* Header */}
        <div style={replyStyles.header}>
          <div style={replyStyles.headerLeft}>
            <span style={replyStyles.headerIcon}></span>
            <span style={replyStyles.headerText}>Reply Processing</span>
          </div>
          <div style={replyStyles.headerRight}>
            <span style={replyStyles.counter}>
              {processedCount} / {totalReplies}
            </span>
            <span style={replyStyles.counterLabel}>Replies Processed</span>
          </div>
        </div>

        {/* Stats Bar */}
        <div style={replyStyles.statsContainer}>
          <div style={replyStyles.statItem}>
            <span style={replyStyles.statIcon}>👍</span>
            <span style={replyStyles.statValue}>{stats.interested}</span>
            <span style={replyStyles.statLabel}>Interested</span>
          </div>
          <div style={replyStyles.statDivider} />
          <div style={replyStyles.statItem}>
            <span style={replyStyles.statIcon}>⏰</span>
            <span style={replyStyles.statValue}>{stats.later}</span>
            <span style={replyStyles.statLabel}>Later</span>
          </div>
          <div style={replyStyles.statDivider} />
          <div style={replyStyles.statItem}>
            <span style={replyStyles.statIcon}>🚫</span>
            <span style={replyStyles.statValue}>{stats.optout}</span>
            <span style={replyStyles.statLabel}>Opt-out</span>
          </div>
          <div style={replyStyles.statDivider} />
          <div style={replyStyles.statItem}>
            <span style={replyStyles.statIcon}>🔇</span>
            <span style={replyStyles.statValue}>{stats.noreply}</span>
            <span style={replyStyles.statLabel}>No Reply</span>
          </div>
        </div>

        {/* Replies List */}
        <div style={replyStyles.listContainer}>
          <div style={replyStyles.listHeader}>
            <span style={replyStyles.listHeaderPatient}>Patient</span>
            <span style={replyStyles.listHeaderMessage}>Reply</span>
            <span style={replyStyles.listHeaderStatus}>Classification</span>
          </div>
          
          <div style={replyStyles.listBody}>
            {processedReplies.map((reply) => (
              <div 
                key={reply.id}
                style={{
                  ...replyStyles.listItem,
                  opacity: reply.status === 'pending' ? 0.3 : 1,
                  borderLeft: reply.status === 'categorized' 
                    ? `3px solid ${getCategoryColor(reply.category)}` 
                    : '3px solid transparent',
                  transition: 'all 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              >
                <span style={replyStyles.patientName}>
                  {reply.patient}
                </span>
                <span style={replyStyles.messageText}>
                  "{reply.message}"
                </span>
                <div style={replyStyles.statusContainer}>
                  {reply.status === 'pending' && (
                    <span style={replyStyles.statusPending}>Waiting...</span>
                  )}
                  {reply.status === 'analyzing' && (
                    <span style={replyStyles.statusAnalyzing}>
                      <span style={replyStyles.analyzingDot} />
                      Analyzing...
                    </span>
                  )}
                  {reply.status === 'categorized' && (
                    <span style={{
                      ...replyStyles.categoryBadge,
                      background: getCategoryBadgeStyle(reply.category).bg,
                      color: getCategoryBadgeStyle(reply.category).text,
                    }}>
                      {getCategoryIcon(reply.category)} {reply.category.charAt(0).toUpperCase() + reply.category.slice(1)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Completion Badge */}
        {isComplete && (
          <div style={replyStyles.completionBadge}>
            <span style={replyStyles.completionIcon}>🤖</span>
            <span style={replyStyles.completionText}>
              All Replies Processed!
            </span>
            <span style={replyStyles.completionSubtext}>
              {processedCount} replies categorized automatically
            </span>
          </div>
        )}

        {/* Footer */}
        <div style={replyStyles.footer}>
          <span style={replyStyles.footerText}>
            {!isComplete 
              ? 'Automatically analyzing patient replies...' 
              : '✓ All replies processed and categorized'}
          </span>
          {!isComplete && (
            <span style={replyStyles.footerSpeed}>
              AI Classification Active
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Styles
const replyStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: '#070E16',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', -apple-system, sans-serif",
    padding: 24,
    overflow: 'hidden',
  },
  glowBg: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 900,
    height: '70%',
    maxHeight: 700,
    background: 'radial-gradient(circle, rgba(45, 155, 138, 0.08) 0%, rgba(45, 155, 138, 0.02) 40%, rgba(45, 155, 138, 0) 70%)',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
    maxWidth: 800,
    width: '100%',
    gap: 16,
  },
  
  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0 4px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  headerIcon: {
    fontSize: 20,
  },
  headerText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  counter: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '-0.5px',
  },
  counterLabel: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.3px',
  },

  // Stats Bar
  statsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    width: '100%',
    padding: '12px 20px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  statIcon: {
    fontSize: 16,
  },
  statValue: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 700,
  },
  statLabel: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 12,
    fontWeight: 500,
    marginLeft: 2,
  },
  statDivider: {
    width: 1,
    height: 24,
    background: 'rgba(255,255,255,0.06)',
  },

  // Replies List
  listContainer: {
    width: '100%',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
    overflow: 'hidden',
    maxHeight: 400,
  },
  listHeader: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr 120px',
    gap: 12,
    alignItems: 'center',
    padding: '12px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.02)',
  },
  listHeaderPatient: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  listHeaderMessage: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  listHeaderStatus: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    textAlign: 'right',
  },
  listBody: {
    padding: '4px 0',
    maxHeight: 340,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,255,255,0.1) transparent',
  },
  listItem: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr 120px',
    gap: 12,
    alignItems: 'center',
    padding: '10px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
  },
  patientName: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 13,
    fontWeight: 500,
  },
  messageText: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
    fontStyle: 'italic',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },

  // Status Styles
  statusPending: {
    color: 'rgba(255,255,255,0.2)',
    fontSize: 12,
    fontWeight: 400,
  },
  statusAnalyzing: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#60A5FA',
    fontSize: 12,
    fontWeight: 500,
  },
  analyzingDot: {
    display: 'inline-block',
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#60A5FA',
    animation: 'pulse 0.6s ease-in-out infinite',
  },

  // Category Badge
  categoryBadge: {
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.3px',
    animation: 'fadeIn 0.4s ease-out',
  },

  // Completion Badge
  completionBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: 'rgba(45, 155, 138, 0.15)',
    border: '1px solid rgba(45, 155, 138, 0.3)',
    borderRadius: 30,
    padding: '10px 24px',
    animation: 'fadeIn 0.6s ease-out',
  },
  completionIcon: {
    fontSize: 20,
  },
  completionText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: 600,
  },
  completionSubtext: {
    color: '#3ABFA8',
    fontSize: 13,
    fontWeight: 500,
    paddingLeft: 12,
    borderLeft: '1px solid rgba(45, 155, 138, 0.3)',
  },

  // Footer
  footer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 4px',
  },
  footerText: {
    color: 'rgba(255,255,255,0.25)',
    fontSize: 13,
    letterSpacing: '0.3px',
  },
  footerSpeed: {
    color: 'rgba(255,255,255,0.2)',
    fontSize: 12,
    fontFamily: "'JetBrains Mono', monospace",
  },
};

// Add keyframes
const replyStyleSheet = document.createElement("style");
replyStyleSheet.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.2); }
  }
  @keyframes fadeIn {
    0% { opacity: 0; transform: scale(0.9); }
    100% { opacity: 1; transform: scale(1); }
  }
`;
document.head.appendChild(replyStyleSheet);